/**
 * One audit-log row: when, who, what, on what, how it ended, and why.
 *
 * Everything that can be `null` on the wire is rendered as **words**:
 *
 *  - `actor_username: null` — the row records who acted (`actor_id` is required and
 *    always present); it simply has no denormalised name. Saying "no username on this
 *    row" keeps that apart from an actor nobody can identify, which is a much more
 *    alarming fact and is not what the null means.
 *  - `resource_type` / `resource_id: null` — the action was not about a resource.
 *    Several `wp_audit_log` verbs are not: a login, a company switch.
 *  - `reason` — four facts, in `ReasonCell`.
 *
 * `success` renders as **icon plus words plus colour**, in that order of importance
 * (`DESIGN.md` §4): a failed administrative action and a successful one must be told
 * apart in a greyscale screenshot, which is a real thing an auditor produces.
 *
 * The detail toggle is a control and it is the **only** control on a row. There is no
 * delete, no edit and no annotate — both tables are append-only and the contract has
 * no write operation at all — and there is specifically no greyed one. See
 * `AuditLogScreen`'s immutability note for why that is said out loud instead.
 */

import { useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import type { MessageKey } from '../../i18n/messages';
import type { AuditLogEntry } from '../../api/types';
import { formatInstant } from '../../i18n/datetime';
import { Badge } from '../../states/Badge';
import { AuditDetail } from './AuditDetail';
import { ReasonCell } from './ReasonCell';

export interface AuditRowProps {
  readonly entry: AuditLogEntry;
  readonly timezone: string;
}

export function AuditRow({ entry, timezone }: AuditRowProps): ReactNode {
  const { t, locale } = useI18n();
  const [open, setOpen] = useState(false);

  const resource =
    typeof entry.resource_type === 'string' && entry.resource_type !== ''
      ? `${entry.resource_type}${typeof entry.resource_id === 'string' ? ` / ${entry.resource_id}` : ''}`
      : null;

  return (
    <>
      {/* Actor · action · resource · time. The sentence an auditor is reading is "who
          did what to what, and when" — so it is read in that order, and the two
          columns that used to lead (the timestamp and the table name) come after the
          event they belong to. Every identifier is mono because they get retyped into
          a second system; `log_type` sits under the action it classifies rather than
          taking a column of its own. */}
      <tr
        data-testid={`k8-audit-row-${entry.id}`}
        data-log-type={entry.log_type}
        {...(open ? { className: 'wp-audit__row--open' } : {})}
      >
        <td className="wp-audit__actor" data-testid={`k8-audit-actor-${entry.id}`}>
          <code className="wp-mono">{entry.actor_id}</code>{' '}
          {typeof entry.actor_username === 'string' && entry.actor_username !== '' ? (
            entry.actor_username
          ) : (
            <span className="wp-small wp-muted">{t('k8.audit.actor.noName')}</span>
          )}
        </td>
        <td className="wp-audit__action">
          <code className="wp-mono">{entry.action}</code>
          <span
            className="wp-audit__type wp-small wp-muted"
            data-testid={`k8-audit-type-${entry.id}`}
          >
            {t(`k8.audit.type.${entry.log_type}` as MessageKey)}
          </span>
        </td>
        <td className="wp-audit__resource" data-testid={`k8-audit-resource-${entry.id}`}>
          {resource === null ? (
            <span className="wp-small wp-muted">{t('k8.audit.resource.none')}</span>
          ) : (
            <code className="wp-mono">{resource}</code>
          )}
        </td>
        <td className="wp-audit__when">{formatInstant(entry.occurred_at, locale, timezone)}</td>
        <td>
          <Badge
            severity={entry.success ? 'low' : 'high'}
            icon={entry.success ? 'circle-check' : 'triangle-alert'}
            label={entry.success ? t('k8.audit.result.success') : t('k8.audit.result.failure')}
            testId={`k8-audit-result-${entry.id}`}
          />
        </td>
        <td className="wp-audit__reason">
          <ReasonCell entry={entry} />
        </td>
        <td>
          <button
            className="wp-button wp-button--quiet wp-button--sm"
            type="button"
            aria-expanded={open}
            data-testid={`k8-audit-toggle-${entry.id}`}
            onClick={() => {
              setOpen((value) => !value);
            }}
          >
            {open
              ? t('k8.audit.detail.hide', { id: entry.id })
              : t('k8.audit.detail.show', { id: entry.id })}
          </button>
        </td>
      </tr>
      {/* The detail opens **under its own row**, not in a modal: reading an audit row
          is a comparison with the rows around it, and a dialog takes them away. */}
      {open ? (
        <tr className="wp-audit__row--open">
          <td className="wp-audit__detail-cell" colSpan={7}>
            <AuditDetail entry={entry} />
          </td>
        </tr>
      ) : null}
    </>
  );
}
